import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useActionHistory } from '../api/queries'
import type { ActionRow } from '../api/types'
import { ActionBadge } from '../components/ActionBadge'
import { relativeTime } from '../utils/relative-time'

/**
 * Models whose record IDs do NOT exist in Odoo — they live in the
 * middleware's own DB (customer_product_mappings etc.). Linking to
 * Odoo's web view for these produces a KeyError (observed prod
 * 2026-04-24 on `product.customerinfo`). Render these as plain text
 * instead of a link.
 *
 * - `middleware.*` is the forward-going convention (chat.py:284+).
 * - `product.customerinfo` is legacy-labeled existing audit rows —
 *   keep the coerce for backward compat until/unless we migrate
 *   historical audit_log entries.
 */
function isMiddlewareLocalModel(model: string): boolean {
  return model.startsWith('middleware.') || model === 'product.customerinfo'
}

type ActionFilter = 'all' | string
type UserFilter = 'all' | string

export function ActionHistoryPage() {
  const { data, isLoading, isFetching, isError, error, refetch } = useActionHistory(500)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all')
  const [userFilter, setUserFilter] = useState<UserFilter>('all')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const rows = useMemo<ActionRow[]>(() => data?.actions ?? [], [data])

  const actionOptions = useMemo(() => {
    return Array.from(new Set(rows.map((r) => r.action))).sort()
  }, [rows])
  const userOptions = useMemo(() => {
    return Array.from(new Set(rows.map((r) => r.user).filter(Boolean))).sort()
  }, [rows])

  const filtered = useMemo<ActionRow[]>(() => {
    const needle = debounced.trim().toLowerCase()
    return rows.filter((r) => {
      if (actionFilter !== 'all' && r.action !== actionFilter) return false
      if (userFilter !== 'all' && r.user !== userFilter) return false
      if (!needle) return true
      return (
        r.summary.toLowerCase().includes(needle) ||
        r.user.toLowerCase().includes(needle) ||
        r.action.toLowerCase().includes(needle) ||
        r.odoo_model.toLowerCase().includes(needle)
      )
    })
  }, [rows, debounced, actionFilter, userFilter])

  const last24hCount = useMemo(() => {
    // `data.actions` already refetches on the query's stale cadence, so
    // using the current wall-clock once per render here is correct.
    // eslint-disable-next-line react-hooks/purity
    const cutoff = Date.now() - 24 * 3600 * 1000
    return rows.filter((r) => {
      const t = Date.parse(r.created_at)
      return !Number.isNaN(t) && t >= cutoff
    }).length
  }, [rows])

  const columns = useMemo<ColumnDef<ActionRow, unknown>[]>(
    () => [
      {
        id: 'when',
        header: 'When',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-xs text-secondary">
            {relativeTime(ctx.getValue() as string)}
          </span>
        ),
      },
      { id: 'user', header: 'User', accessorKey: 'user' },
      {
        id: 'action',
        header: 'Action',
        cell: (ctx) => <ActionBadge action={ctx.row.original.action} />,
      },
      {
        id: 'odoo_model',
        header: 'Model',
        accessorKey: 'odoo_model',
        cell: (ctx) => (
          <span className="text-role-metadata">
            {ctx.getValue() as string}
          </span>
        ),
      },
      {
        id: 'record_id',
        header: 'Record',
        cell: (ctx) => {
          const { odoo_model, record_id } = ctx.row.original
          // Middleware-local records (customer_product_mappings etc.)
          // don't resolve in Odoo — render plain text to avoid the
          // Odoo KeyError screen users hit when clicking them.
          if (isMiddlewareLocalModel(odoo_model)) {
            return (
              <span
                className="text-secondary"
                title="Record lives in CARLA's database — not an Odoo record"
              >
                #{record_id}
              </span>
            )
          }
          return (
            <a
              href={odooUrl(odoo_model, record_id)}
              target="_blank"
              rel="noreferrer"
              className="text-link hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              #{record_id}
            </a>
          )
        },
      },
      {
        id: 'summary',
        header: 'Summary',
        accessorKey: 'summary',
        cell: (ctx) => (
          <span className="block max-w-[520px] truncate text-sm text-primary">
            {ctx.getValue() as string}
          </span>
        ),
      },
    ],
    [],
  )

  // `/` focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Action History"
        description="Audit log of CARLA-initiated actions on Odoo records."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Total" value={rows.length.toLocaleString()} />
          <MetricCard variant="inline" label="Last 24h" value={last24hCount.toLocaleString()} />
          <MetricCard variant="inline" label="Users" value={userOptions.length.toLocaleString()} />
          <MetricCard variant="inline" label="Action types" value={actionOptions.length.toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search action, user, order…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <Select
              aria-label="Action type"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as ActionFilter)}
            >
              <option value="all">All actions</option>
              {actionOptions.map((a) => (
                <option key={a} value={a}>
                  {a.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
            <ToolbarDivider />
            <Select
              aria-label="User"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value as UserFilter)}
            >
              <option value="all">All users</option>
              {userOptions.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}
            </ToolbarCount>
            {(search || actionFilter !== 'all' || userFilter !== 'all') ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('')
                  setActionFilter('all')
                  setUserFilter('all')
                }}
                title="Clear search + all filter selects"
              >
                <X size={12} /> Reset
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
          <DataTable
            error={isError ? error : undefined}
            onRetry={() => void refetch()}
            errorSubject="action history"
            data={filtered}
            columns={columns}
            loading={isLoading}
            getRowId={(r) => r.id}
            moduleId="action-history"
            onRowClick={(r) => {
              // Only open in Odoo when the record actually lives there.
              // Middleware-local records render as plain text in the
              // Record column; a click-through here would Odoo-KeyError.
              if (isMiddlewareLocalModel(r.odoo_model)) return
              window.open(odooUrl(r.odoo_model, r.record_id), '_blank')
            }}
            maxHeight="100%"
            columnWidths={{
              when: 110,
              user: 110,
              action: 220,
              odoo_model: 200,
              record_id: 90,
            }}
            emptyState={
              <EmptyState
                variant={rows.length === 0 ? 'no-data' : 'no-match'}
                title={rows.length === 0 ? 'No actions recorded yet' : 'No matches'}
                description={
                  rows.length === 0
                    ? 'CARLA-executed actions against Odoo appear here after they complete.'
                    : 'Try clearing the action or user filter.'
                }
              />
            }
          />
        </div>
      </div>
    </div>
  )
}
